<template>
    <Teleport to="body">
        <div v-if="visible" class="modal-overlay" @click="close">
            <div class="modal-content" @click.stop>
                <div class="modal-header">
                    <h3 class="modal-title">{{ title }}</h3>
                    <div class="modal-header-actions">
                        <a
                            v-if="showEdit && editDescriptionLink"
                            :href="editDescriptionLink"
                            target="_blank"
                            class="btn-edit"
                            title="Edit Description"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            >
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                        </a>
                        <button class="modal-close" @click="close" title="Close">×</button>
                    </div>
                </div>
                <div class="modal-body">
                    <div class="page-description" v-html="description"></div>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script setup lang="ts">
interface Props {
    visible: boolean;
    title: string;
    description: string;
    editDescriptionLink?: string;
    showEdit?: boolean;
}

interface Emits {
    "update:visible": [value: boolean];
}

defineProps<Props>();
const emit = defineEmits<Emits>();

const close = () => {
    emit("update:visible", false);
};
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 1rem;
}

.modal-content {
    background: var(--color-bg-white);
    border-radius: 0.75rem;
    box-shadow:
        0 20px 25px -5px rgba(0, 0, 0, 0.1),
        0 10px 10px -5px rgba(0, 0, 0, 0.04);
    max-width: 1100px;
    width: 100%;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    border: 1px solid var(--color-border);
}

.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.5rem;
    border-bottom: 1px solid var(--color-border);
}

.modal-title {
    margin: 0;
    color: var(--color-text-primary);
    font-size: 1.25rem;
    font-weight: 600;
}

.modal-header-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.btn-edit {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    color: var(--color-text-secondary);
    text-decoration: none;
    transition: all 0.2s ease;
}

.btn-edit:hover {
    background: var(--color-bg-hover);
    color: var(--color-primary);
}

.modal-close {
    background: none;
    border: none;
    font-size: 1.5rem;
    color: var(--color-text-secondary);
    cursor: pointer;
    border-radius: 50%;
    width: 2rem;
    height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
}

.modal-close:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
}

.modal-body {
    padding: 1.5rem;
    overflow-y: auto;
}

.page-description {
    background: white;
    border-radius: 0.5rem;
    padding: 0.5rem;
    overflow: auto;
    line-height: 1.6;
    color: black;
}

.page-description :deep(b),
.page-description :deep(strong) {
    font-weight: bold !important;
}

.page-description :deep(h1),
.page-description :deep(h2),
.page-description :deep(h3),
.page-description :deep(h4),
.page-description :deep(h5),
.page-description :deep(h6) {
    margin-top: 1.5rem;
    margin-bottom: 0.5rem;
    font-weight: 600;
    color: black;
}

.page-description :deep(h1:first-child),
.page-description :deep(h2:first-child),
.page-description :deep(h3:first-child) {
    margin-top: 0;
}

.page-description :deep(p) {
    margin: 0.75rem 0;
}

.page-description :deep(ul),
.page-description :deep(ol) {
    margin: 0.75rem 0;
    padding-left: 1.5rem;
}

.page-description :deep(li) {
    margin: 0.25rem 0;
}

.page-description :deep(a) {
    color: black;
    text-decoration: none;
}

.page-description :deep(a:hover) {
    text-decoration: underline;
}

.page-description :deep(img) {
    max-width: 100%;
    height: auto;
    border-radius: 0.5rem;
    margin: 1rem 0;
}

.page-description :deep(code) {
    background: white;
    padding: 0.125rem 0.25rem;
    border-radius: 0.25rem;
    font-size: 0.875em;
}

.page-description :deep(pre) {
    background: white;
    padding: 1rem;
    border-radius: 0.5rem;
    overflow-x: auto;
    margin: 1rem 0;
}

.page-description :deep(pre code) {
    background: none;
    padding: 0;
}

.page-description :deep(blockquote) {
    border-left: 4px solid white;
    padding-left: 1rem;
    margin: 1rem 0;
    color: black;
    font-style: italic;
}

.page-description :deep(table) {
    width: 100%;
    border-collapse: collapse;
    margin: 1rem 0;
}

.page-description :deep(th),
.page-description :deep(td) {
    border: 1px solid black;
    padding: 0.5rem;
    text-align: left;
}

.page-description :deep(th) {
    background: white;
    font-weight: 600;
}
</style>
